import { useEffect, useState } from "react";
import { Crown, Sunrise } from "lucide-react";
import { PageHeader } from "@/components/ranger/PageHeader";
import { ConfigForm } from "@/components/ranger/Crud";
import { PerfGraphs } from "@/components/servertools/PerfGraphs";
import { FeatureSwitches } from "@/components/servertools/FeatureSwitches";
import { AdminNotes } from "@/components/servertools/AdminNotes";
import { DayVotes, Kings } from "@/components/servertools/History";
import { channelOptions } from "@/pages/Feeds";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const voteFields = [
  { key: "day_vote_auto", label: "Start a vote automatically at nightfall", type: "switch" },
  { key: "day_vote_phrase", label: "Vote Quick Chat phrase", hint: "Players send this Quick Chat line to vote, e.g. Yes" },
  { key: "day_vote_percent", label: "Votes needed (% of online players)", type: "number" },
  { key: "day_vote_min_votes", label: "Minimum votes", type: "number" },
  { key: "day_vote_seconds", label: "Vote length (seconds)", type: "number" },
  { key: "day_vote_cooldown_minutes", label: "Minutes between votes", type: "number" },
  { key: "night_start_hour", label: "Night starts (game hour)", type: "number" },
  { key: "night_end_hour", label: "Night ends (game hour)", type: "number" },
  { key: "day_hour", label: "Skip to (game hour)", type: "number" },
];

const kingFields = (chans, roles) => [
  { key: "king_ingame", label: "Announce in game (say)", type: "switch" },
  { key: "king_reward", label: "Coin reward", type: "number" },
  { key: "king_channel_id", label: "Discord channel", type: "select", options: channelOptions(chans), hint: "Off uses the leaderboard channel." },
  { key: "king_role_id", label: "Crown role (moves to each new king)", type: "select", options: [["off", "None"], ...roles.map((r) => [r.id, r.name])] },
];

export default function ServerTools() {
  const { active, can } = useAuth();
  const [chans, setChans] = useState(null);
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    if (!can("admin")) { setChans([]); return; }
    api.get(`/communities/${active.id}/discord/channels`).then((r) => setChans(r.data)).catch(() => setChans([]));
    api.get(`/communities/${active.id}/discord/roles`).then((r) => setRoles(r.data)).catch(() => setRoles([]));
  }, [active.id, can]);
  return (
    <div>
      <PageHeader eyebrow="Server" title="Server Tools" description="Live performance graphs, day voting, King of the Day, feature switches and private staff notes." />
      <PerfGraphs cid={active.id} />
      <FeatureSwitches cid={active.id} />
      <p className="mb-4 text-xs text-slate-500">Turn Day voting and King of the Day on or off with the Feature switches above. Day voting uses env.time (unverified on RCE): test Set time of day on the Live Console first. Kings are crowned at midnight US Central.</p>
      {chans && (
        <div className="grid gap-5 xl:grid-cols-2">
          <ConfigForm cid={active.id} field="servertools" testId="dayvote-config-card" fields={voteFields} title="Day voting" icon={Sunrise} />
          <ConfigForm key={`${chans.length}-${roles.length}`} cid={active.id} field="servertools" testId="king-config-card" fields={kingFields(chans, roles)} title="King of the Day" icon={Crown} />
        </div>
      )}
      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <DayVotes cid={active.id} />
        <Kings cid={active.id} />
      </div>
      {can("moderator") && <AdminNotes cid={active.id} />}
    </div>
  );
}
